/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [amended C3a §4] THE UN-PUBLISH VOCABULARY, MOVED VERBATIM FROM `pages/ArrangementDetail`.
 *
 *  ⚠️ ONE VOCABULARY, BECAUSE THERE IS ONE CONTROL. §4 hid the record page and would have taken
 *  `Take back to draft` with it; the flow is extracted rather than copied — `ARCHIVE_COPY`'s own
 *  move earlier in this pass — so the two callers cannot describe the same act differently.
 *
 *  ⚠️ THE STRINGS ARE UNCHANGED, AND THAT IS DELIBERATE. `journeyRaise.spec.ts` and
 *  `ArrangementDetail.test.tsx` address several of them by TEXT, so a move that also reworded
 *  would make a red test ambiguous between the two.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const UNPUBLISH_COPY = {
  action: 'Take back to draft',
  /**
   * ⚠️ "Take back to draft" RATHER THAN "Un-publish" OR "Withdraw". An operator who reads
   * "withdraw" will look for the order in the Archive, where it is not — it is still a current
   * order. The label says where it goes.
   */
  hint: 'Take this order back to draft so its duty points can be changed.',
  title: 'Take this order back to draft?',
  ok: 'Take back to draft',
  reasonLabel: 'Why is this being taken back?',
  /** ⚠️ Required. It reaches the audit row, which is where "why" is asked a week later. */
  reasonHint: 'This is recorded against the order.',
  /** Tier FREE — nobody has been told about a specific person. */
  free: 'Nobody has been named yet, so no one has to be told.',
  /** Tier DISCARDS_ASSIGNMENTS — the two numbers, because they are different questions. */
  discards: (assignments: number, officers: number): string =>
    `${String(assignments)} name${assignments === 1 ? '' : 's'} already entered will be discarded, ` +
    `affecting ${String(officers)} officer${officers === 1 ? '' : 's'}.`,
  /**
   * ⚠️ [C6a] A COMMANDER A DIVISION NAMED, SAID WITHOUT A NUMBER. `unpublishImpact.commandSeatsNamed`
   * is a flag because command enters no figure (FR-5.2.6b) — so this sentence carries no count, and
   * it is what stops an order whose only names are commanders from reading "0 names … will be
   * discarded". The desks that named them are `commandTold`'s.
   */
  commandDiscarded:
    'Commanders the divisions named will be discarded, and each of them will be told it has been taken back.',
  /**
   * ⚠️ [C6a review] WHO ELSE HEARS, SAID WITHOUT A NUMBER. The withdrawal event carries `toldRecipients`
   * — every desk asked for a command position, named or not — while `told`'s figure stays the force's.
   * Without it an unnamed ask of North beside "1 division and unit will be told" told three desks while
   * saying one. `unpublishImpact.commandSeatsAsked` is the flag.
   */
  commandTold: 'Every division and unit asked for a command role will be told too.',
  told: (units: number): string =>
    `${String(units)} division${units === 1 ? '' : 's'} and unit${units === 1 ? '' : 's'} will be told it has been ` +
    'taken back.',
  duties: (duties: number): string =>
    `${String(duties)} ${duties === 1 ? 'duty' : 'duties'} will be discarded. Publishing again recreates them.`,
  /** Tier BLOCKED — the same refusal archive raises, said before the click rather than after. */
  blocked: 'This cannot be taken back because one of its duties has already started.',
  blockedEvent: 'A duty under this order has already been carried out, so it can no longer be changed.',
  /**
   * ⚠️ [C2b §4g] Its OWN duty and name counts — never folded into the parent's.
   *
   * ⚠️ [C6a] `commandNamed` IS THE MEMBER'S OWN FLAG, AND IT IS REQUIRED rather than defaulted
   * `false` — a forgotten default is how a corrigendum whose only names are commanders read
   * "0 names discarded with it". The commanders are named without a number, and with no names the
   * force clause is dropped rather than stated as zero. Without the flag the sentence is unchanged.
   */
  cascade: (reference: string, duties: number, names: number, commandNamed: boolean): string => {
    const dutyClause = `${duties} ${duties === 1 ? 'duty' : 'duties'}`;
    const nameClause = `${names} ${names === 1 ? 'name' : 'names'}`;
    if (!commandNamed)
      return `${reference} goes back to draft too — ${dutyClause} and ${nameClause} discarded with it.`;
    const commanders = 'the commanders its divisions named';
    return names === 0
      ? `${reference} goes back to draft too — ${dutyClause} and ${commanders} discarded with it.`
      : `${reference} goes back to draft too — ${dutyClause}, ${nameClause} and ${commanders} discarded with it.`;
  },
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7B §6b/§6c] TAKING A RUNNING ORDER BACK TO DRAFT — A `master_superadmin` OVERRIDE, AND A
 *  DELIBERATE PIERCING OF [C1d]'s RULE THAT A RUNNING ORDER IS A RECORD.
 *
 *  ⚠️ IT IS NOT "TAKE BACK TO DRAFT". That act is refused once a duty has started; this one is offered
 *  exactly there, and it DELETES what the ground has already recorded — markings and notifications
 *  included. Every count it states is exact and is counted again on the server; if any moved after the
 *  dialog was read, the server refuses and the dialog says so.
 *
 *  ⚠️ COMMAND IS A YES/NO, NEVER A NUMBER (FR-5.2.6b) — `inChargeNamed` is worded, not counted.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const REVERT_COPY = {
  action: 'Take the running order back to draft',
  title: 'Take this running order back to draft?',
  ok: 'Take it back to draft',
  checking: 'Counting what this will delete…',
  intro: 'A duty under this order has started. Taking it back deletes everything it has put on the ground:',
  duties: (count: number): string => `${String(count)} dut${count === 1 ? 'y' : 'ies'} will be deleted.`,
  emptySlots: (count: number): string => `${String(count)} empty position${count === 1 ? '' : 's'} will be deleted.`,
  names: (names: number, officers: number): string =>
    `${String(names)} name${names === 1 ? '' : 's'} on duties will be discarded, affecting ${String(officers)} officer${
      officers === 1 ? '' : 's'
    }.`,
  markings: (count: number): string =>
    `${String(count)} marking${count === 1 ? '' : 's'} already recorded on the ground will be deleted.`,
  notifications: (count: number): string =>
    `${String(count)} duty notification${count === 1 ? '' : 's'} already sent will be deleted.`,
  inChargeNamed: 'An in-charge was named on it; that naming is discarded too.',
  inChargeNotNamed: 'No in-charge was named on it.',
  told: 'Everyone named on it, and the nodal officers of every unit it was addressed to, are told it was taken back.',
  reasonLabel: 'Why is this being taken back?',
  reasonHint: 'Required. It is recorded against the order with every count above, and shown on the order.',
  changed: 'The counts changed since this was opened — they have been counted again. Check them and confirm once more.',
  done: 'Taken back to draft',
} as const;

/**
 * [P7B §6c] DELETE A REVERTED DRAFT FOR GOOD — the ordinary Discard, then the ordinary permanent delete,
 * offered to a `master_superadmin` holder on a draft a running order was taken back into.
 */
export const DELETE_REVERTED_COPY = {
  action: 'Delete permanently',
  title: 'Delete this order permanently?',
  body: 'It is discarded and then deleted. It cannot be restored afterwards.',
  ok: 'Delete permanently',
  done: 'Deleted',
} as const;
